import * as React from 'react';
import { formatDateTime, formatUndefined } from '../misc/components/Formatters/Formatters';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import { UnitsToggle } from './UnitsToggle';
import { Table, Column } from '../Components/QueryTable';
import { MatchSortHeader } from '../Components/MatchSortHeader';
import { DateMatchSortHeader } from '../Components/DateMatchSortHeader';
import { WebPortalClient, BloodGlucoseTest } from 'webportalclient';
import { ODataParams, Query, useQuery } from '../Components/Query';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import DocumentTitle from '../misc/components/DocumentTitle';
import { SortHeader } from '../Components/SortHeader';
import { Link } from 'react-router-dom';
import { useWebPortalApiClient } from '../misc/api/WebPortalTokenClient';

export interface BloodGlucoseTestsModalProps {
}

export interface BloodGlucoseTestsModalState {
    showTestNumberFilter: boolean;
    showDateTimeFilter: boolean;
    showSampleMethodFilter: boolean;
    showBloodGlucoseFilter: boolean;
    showBloodKetoneFilter: boolean;
}

export const BloodGlucoseTestsTable = (props: BloodGlucoseTestsModalProps) => {
    const [unitsSelectedMmol, setUnitsSelectedMmol] = React.useState(false);
    const [sampleMethods, setSampleMethods] = React.useState([]);
    const { data, applyFilters } = useQuery();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const getData = async () => {
            const sampleMethodsResult = await client.client.getBloodGlucoseSampleMethods("name");
            const options = sampleMethodsResult.value.map(s => ({
                value: s.name,
                label: s.name,
            }));
            setSampleMethods(options);
        }

        getData();
    }, []);

    const glucoseMember = unitsSelectedMmol ? "glucoseSI" : "glucoseUS";

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const formatGlucose = (row: BloodGlucoseTest) => {

        if (row.glucoseSI === undefined && row.glucoseSI === null && row.glucoseUS === undefined && row.glucoseUS === null) return '-';

        return unitsSelectedMmol
            ? (row.glucoseSI ? row.glucoseSI.toFixed(1) : (row.glucoseUS / 18).toFixed(1))
            : (row.glucoseUS ? row.glucoseUS.toFixed(1) : (row.glucoseSI * 18).toFixed(1));
    }

    const formatKetone = (ketoneValue) => {
        if (ketoneValue === undefined || ketoneValue === null) return '-';

        return ketoneValue.toFixed(1);
    }

    const outOfRangeValueFormatter = (value: number | string): number | string => {
        switch (value) {
            case "999.0":
                return "HI";
            case "-999.0":
                return "LO";
            default:
                return value;
        }
    }

    return (
        <ErrorBoundary>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Blood Glucose Tests</h2>
                </div>
                <div className="col-md-6 form-group">
                    <div className="h2 pull-right">
                        <Link to="/sessions" className="btn btn-primary btn-outline">Back to List</Link>&nbsp;
                        <UnitsToggle isMmol={unitsSelectedMmol} onToggleClick={() => setUnitsSelectedMmol(!unitsSelectedMmol)} />&nbsp;
                        <button className="btn btn-primary" onClick={clearAllFilters}>Clear All Filters</button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive">
                                <Table data={data} rowKeyField="id">
                                    <Column field="index" header={<SortHeader title="Test" field="index" />} formatter={index => index} />
                                    <Column field="occurred" header={<DateMatchSortHeader title="Test Start" field="occurred" sortField="occurred" />} formatter={occurred => formatDateTime(occurred)} />
                                    <Column field="sampleMethodName" header={<MatchSortHeader title="Sample Method" field="sampleMethodName" options={sampleMethods} sortField="sampleMethodName" />} formatter={samplemethodname => samplemethodname} />
                                    <Column field="" header={<SortHeader title={unitsSelectedMmol ? "Glucose (mmol/L)" : "Glucose (mg/dl)"} field={glucoseMember} />} formatter={glucose => outOfRangeValueFormatter(formatUndefined(formatGlucose(glucose)))} />
                                    <Column field="ketone" header={<SortHeader title="Ketone (mmol/L)" field="ketone" />} formatter={ketone => outOfRangeValueFormatter(formatKetone(ketone))} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );    
}

export const BloodGlucoseTestsScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();

    const handleRefresh = async (params: ODataParams) => {

        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `session/visit/subject/site/studyId eq ${study.id}`;

        return await client.client.getBloodGlucoseTests(undefined, filter, params.orderby, 'session($select=visit;$expand=visit($select=subject;$expand=subject($select=site;$expand=site($select=studyId))))', params.top, params.skip, undefined, true);
    }

    return (
        <DocumentTitle title="Blood Glucose Tests | Vitalograph Web Portal">
            <Query onRefresh={handleRefresh} initialOrdering={[{ field: "occurred", condition: "desc" }]}>
                <BloodGlucoseTestsTable />
            </Query>
        </DocumentTitle>
    );
}
